import { useState } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { WithGuideLink } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { RouteName, ActionType, TextKey, FeatureFlag } from '@mews-commander/enums';
import { lt, useFeatureFlag } from '@mews/framework';
import { IconName } from '@optimus-web/icons';
import { compactArray } from '@mews/fp';
import { RoutingRuleTriggerType } from '@mews-commander/data-access';
import { useTableState } from '@optimus-web/core';
import { checkEnterprisePrivilege } from '@mews-commander/legacy-entities/src/selectors/session/privilegeChecks';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    NewTabLink,
    PageLayout,
    TooltippedIconButton,
    useBreadcrumbs,
    useEnterpriseNavigate,
} from '@mews-commander/ui';
import { FilterSidebarLayout } from '../../../components/layout';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import {
    routingRulesListFilterSchema,
    RoutingRulesListFilterSchemaKeys,
    RoutingRulesListFilterValues,
} from '../schemas/routingRulesListFilterSchema';
import { useRoutingRuleListFilterDefaultValues } from '../utils/useRoutingRuleListFilterDefaultValues';
import { RoutingRuleListFilters } from '../components/RoutingRuleListFilters';
import { RoutingRulesTable } from '../components/RoutingRulesTable/RoutingRulesTable';
import { useRoutingRuleTableData } from '../utils/useRoutingRulesTableData';
import { RoutingRuleListTableRowData } from '../components/RoutingRulesTable/types';

/* can be removed when  FeatureFlag.granularRoutingRulesConditionsExtensionEnabled is done */
const emptyFunc = () => {};

type RoutingRulesListViewProps = WithGuideLink;

export const RoutingRulesListView: React.FC<RoutingRulesListViewProps> = ({ guideLink }) => {
    const resolveRoute = useResolveRoute();
    const navigate = useEnterpriseNavigate();

    const { id, name } = useCurrentEnterprise();
    const enterpriseUrl = resolveRoute(RouteName.EnterpriseDetail, { enterpriseId: id });

    const hasEnterprisePrivilege = useCommanderSelector((state) =>
        checkEnterprisePrivilege(state, { enterpriseId: id })
    );
    const defaultFilterValues = useRoutingRuleListFilterDefaultValues();

    const [filters, setFilters] = useState<RoutingRulesListFilterValues>(defaultFilterValues);

    const [tableState, setTableState] = useTableState<RoutingRuleListTableRowData>({
        pagination: { show: false },
    });

    const { data, isFetching, displayLegacy } = useRoutingRuleTableData(filters, tableState);

    const newRoutingRuleUrl = resolveRoute(RouteName.RoutingRuleCreate, { query: { EnterpriseId: id } });

    const actions = compactArray<HeadingAction | null>([
        hasEnterprisePrivilege
            ? {
                  url: newRoutingRuleUrl,
                  icon: IconName.PlusSimple,
                  text: lt('RoutingRulesAddButtonLabel'),
                  type: ActionType.Link,
              }
            : null,
    ]);

    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const breadcrumbs = useBreadcrumbs();

    if (granularRoutingRulesConditionsExtensionEnabled) {
        return (
            <PageLayout
                breadcrumbs={breadcrumbs}
                guideLink={guideLink}
                actions={
                    hasEnterprisePrivilege && (
                        <NewTabLink data-test-id="create-new-routing-rule" url={newRoutingRuleUrl}>
                            <TooltippedIconButton
                                icon={IconName.PlusSimple}
                                tooltip={lt('Create')}
                                type="button"
                                variant="tertiary"
                                size="medium"
                            />
                        </NewTabLink>
                    )
                }
            >
                <RoutingRulesTable
                    showTableFilters
                    displayLegacy={displayLegacy}
                    data={data}
                    loading={isFetching}
                    tableState={tableState}
                    onTableStateChange={setTableState}
                    onFiltersSubmit={(values) => {
                        setFilters(values);
                        void navigate({
                            to: 'RoutingRule/Index',
                            params: { currentEnterpriseId: id },
                            search: values,
                        });
                    }}
                />
            </PageLayout>
        );
    }

    return (
        <FilterSidebarLayout
            padded={false}
            breadcrumbs={[
                { text: name, url: enterpriseUrl },
                { text: lt(TextKey.BillingAutomationPlural), active: true },
            ]}
            actions={actions}
            loading={isFetching}
            title={lt('BillingAutomationPlural')}
            filterSchema={routingRulesListFilterSchema}
            guideLink={guideLink}
            defaultFilterValues={
                displayLegacy
                    ? defaultFilterValues
                    : {
                          ...defaultFilterValues,
                          timing: defaultFilterValues[RoutingRulesListFilterSchemaKeys.Timing]?.filter(
                              (value) => value !== RoutingRuleTriggerType.Legacy
                          ),
                      }
            }
            onFilterSubmit={setFilters}
            filterSidebarOpenedByDefault={false}
            filterSidebar={{
                title: lt('Filter'),
                fields: <RoutingRuleListFilters displayLegacy={displayLegacy} />,
            }}
        >
            <RoutingRulesTable
                displayLegacy={displayLegacy}
                data={data}
                loading={isFetching}
                tableState={tableState}
                onTableStateChange={setTableState}
                onFiltersSubmit={emptyFunc}
            />
        </FilterSidebarLayout>
    );
};
